import React from 'react';
import { useTranslation } from 'react-i18next';

import { AssetManager } from '../../../../../../viewer/asset-manager';
import { ReferencePlane } from '../../../../../../viewer/elements/reference-plane/reference-plane';
import { SubscriptionTopic } from '../../../../../../viewer/messaging/pubsub';
import { useNonce } from '../../../../../hooks/use-nonce';
import { useSubscribe, useSubscribeCallback } from '../../../../../hooks/use-subscribe';
import { SkyviewContext } from '../../../../skyview/skyview-state';
import { ToolIcon } from '../../../../skyview-new/toolbar/tool-icon';
import { WrapWithSettingsIcon } from '../../settings/wrap-with-settings-icon';
import { Settings } from './settings';

type Props = {
  toolbarRef: React.RefObject<HTMLDivElement>;
};

const ReferencePlaneToolIcon = (props: Props) => {
  const { t } = useTranslation();
  const [_, refresh] = useNonce();
  const { skyview } = React.useContext(SkyviewContext);

  const toggle = () => {
    ReferencePlane.instance.Visibility = !ReferencePlane.instance.Visibility;
    refresh();
  };

  const isEnabled = skyview.isPerspectiveViewActive();
  const isActive = isEnabled && ReferencePlane.instance.Visibility;

  useSubscribe(SubscriptionTopic.OnSceneBoundingBoxChanged, ({ boundingBox }) => {
    // The SkyView scene can switch to its perspective camera later
    if (skyview.isTimelineViewActive()) {
      ReferencePlane.instance.create(boundingBox);
      AssetManager.instance.add(ReferencePlane.instance);
    }
  });

  useSubscribeCallback(SubscriptionTopic.OnCameraChanged, () => {
    if (!skyview.isPerspectiveViewActive()) {
      ReferencePlane.instance.Visibility = false;
    }
    refresh();
  });

  useSubscribe(SubscriptionTopic.OnSceneChangeStarted, () => ReferencePlane.instance.destroy());

  return (
    <WrapWithSettingsIcon
      content={<Settings />}
      disabled={!isActive}
      toolbarRef={props.toolbarRef}
      tooltip={{
        text: t('referencePlane.settings.tooltip.text', { ns: 'skyviewToolbar' }),
        title: t('referencePlane.tooltip.title', { ns: 'skyviewToolbar' }),
      }}
    >
      <ToolIcon
        active={isActive}
        callback={toggle}
        disabled={!isEnabled}
        icons={[
          ['fad', 'dash'],
          ['fal', 'arrows-up-down'],
        ]}
        tooltip={{
          title: t('referencePlane.tooltip.title', { ns: 'skyviewToolbar' }),
          text: t('referencePlane.tooltip.text', { ns: 'skyviewToolbar' }),
        }}
      />
    </WrapWithSettingsIcon>
  );
};

export { ReferencePlaneToolIcon };
